import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import apiService from 'app/apiService';
import { notificationAlertActions } from 'app/pages/NotificationAlert/slice';
import socketServiceManager from 'app/socketService';
import { EmailTemplatesSocketService } from 'app/socketService/emailTemplates/emailTemplatesSocketService';
import {
  appendNurturingAutomationSocketMessage,
  clearNurturingAutomationState,
  loadNurturingAutomationState,
  saveNurturingAutomationState,
  updateNurturingAutomationProgress,
} from 'app/pages/TestNurturingPage/utils/testNurturingAutomationPersistence';
import {
  clearHilRetryAllState,
  loadHilRetryAllState,
  saveHilRetryAllState,
  updateHilRetryAllLiveStatus,
} from 'app/pages/HumanInTheLoopPage/utils/hilSegmentsRetryAllPersistence';
import {
  getAttentionRetryAllLiveProgress,
  getNurturingAutomationJobId,
  getNurturingAutomationSocketMessage,
  getNurturingAutomationLeadCounts,
  getNurturingAutomationStatusText,
  shouldUpdateAttentionRetryAllUi,
  NURTURING_AUTOMATION_EVENT_COMPLETED,
  NURTURING_AUTOMATION_EVENT_DEALER_PROGRESS,
  NURTURING_AUTOMATION_EVENT_LEAD_PROGRESS,
  NURTURING_AUTOMATION_EVENT_PREFIX,
  NURTURING_AUTOMATION_EVENT_REQUEST_RECEIVED,
} from 'app/utils/nurturingTestAutomationProgressUtils';
import type { NurturingAutomationSocketMessage } from 'app/utils/nurturingTestAutomationProgressUtils';

export type StartNurturingAutomationSessionOptions = {
  jobIds: string[];
  progressTotal?: number;
  /** Used when persisting a single-job session (Test Nurturing). */
  primaryJobId?: string;
};

export type PrepareNurturingAutomationSessionOptions = {
  progressTotal?: number;
};

export type UseNurturingTestAutomationProgressOptions = {
  dealerId: string;
  canAccess: boolean;
  onComplete?: () => void;
  enablePersistence?: boolean;
  invalidatePrefixesOnComplete?: Array<{ prefix: string; method?: string }>;
  progressTitle?: string;
  progressSubtitle?: string;
  /** Unit label in default progress subtitle (e.g. leads, segments). */
  progressCountLabel?: string;
  /** HIL retry-all uses live-updating status + separate localStorage key. */
  persistenceVariant?: 'nurturing-test' | 'hil-retry-all';
  onSessionRestored?: () => void;
};

/** Stable default — avoids new array each render when callers omit the option. */
const DEFAULT_INVALIDATE_PREFIXES_ON_COMPLETE: Array<{
  prefix: string;
  method?: string;
}> = [
  { prefix: 'api/v2/leads', method: 'GET' },
  { prefix: 'api/v2/nurturing-automation/opportunities', method: 'GET' },
];

export function useNurturingTestAutomationProgress(
  options: UseNurturingTestAutomationProgressOptions,
) {
  const {
    dealerId,
    canAccess,
    onComplete,
    invalidatePrefixesOnComplete:
      invalidatePrefixesOnCompleteOption,
    progressTitle = 'Running nurturing test...',
    progressSubtitle,
    progressCountLabel = 'leads',
    persistenceVariant,
    onSessionRestored,
  } = options;

  const enablePersistence =
    options.enablePersistence ?? persistenceVariant === 'nurturing-test';

  const invalidatePrefixesOnComplete =
    invalidatePrefixesOnCompleteOption ??
    DEFAULT_INVALIDATE_PREFIXES_ON_COMPLETE;

  const dispatch = useDispatch();
  const [automationInProgress, setAutomationInProgress] = useState(false);
  const [automationProgress, setAutomationProgress] = useState({
    current: 0,
    total: 1,
  });
  const [automationSocketMessages, setAutomationSocketMessages] = useState<
    NurturingAutomationSocketMessage[]
  >([]);
  const [automationCompletionPayload, setAutomationCompletionPayload] =
    useState<unknown>(null);
  const [automationLatestStatusText, setAutomationLatestStatusText] = useState('');
  const [automationLatestStepLabel, setAutomationLatestStepLabel] = useState('');
  const [automationLastUpdatedAt, setAutomationLastUpdatedAt] = useState<number | null>(
    null,
  );
  const [progressPercentageOverride, setProgressPercentageOverride] = useState<
    number | null
  >(null);

  const automationInProgressRef = useRef(false);
  const activeJobIdsRef = useRef<Set<string>>(new Set());
  const pendingJobIdsRef = useRef<Set<string>>(new Set());
  const progressTotalFallbackRef = useRef(1);
  const onCompleteRef = useRef(onComplete);
  const perJobProgressRef = useRef<Map<string, { current: number; total: number }>>(
    new Map(),
  );
  const subscribedJobIdsRef = useRef<Set<string>>(new Set());
  const socketServiceRef = useRef<EmailTemplatesSocketService | null>(null);
  const unsubscribeJobTopicsRef = useRef<() => void>(() => {});
  const dealerIdRef = useRef(dealerId);
  const enablePersistenceRef = useRef(enablePersistence);
  const invalidatePrefixesOnCompleteRef = useRef(invalidatePrefixesOnComplete);
  const aggregateProgressFromJobsRef = useRef<() => {
    current: number;
    total: number;
  }>(() => ({ current: 0, total: 1 }));
  const finalizeSessionRef = useRef<(completionData: unknown) => void>(() => {});
  const sessionFinalizedRef = useRef(false);
  const hilStickyProgressRef = useRef({
    segmentIndex: 0,
    segmentTotal: 1,
    lastPercentage: 0,
  });
  const persistenceVariantRef = useRef(persistenceVariant);
  const onSessionRestoredRef = useRef(onSessionRestored);

  dealerIdRef.current = dealerId;
  enablePersistenceRef.current = enablePersistence;
  invalidatePrefixesOnCompleteRef.current = invalidatePrefixesOnComplete;
  persistenceVariantRef.current = persistenceVariant;
  onSessionRestoredRef.current = onSessionRestored;

  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    automationInProgressRef.current = automationInProgress;
  }, [automationInProgress]);

  useEffect(() => {
    if (!dealerId || !canAccess) return;

    if (persistenceVariant === 'hil-retry-all') {
      const persisted = loadHilRetryAllState(dealerId);
      if (!persisted?.automationInProgress || !persisted.jobId) {
        return;
      }
      const jobSet = new Set([persisted.jobId]);
      activeJobIdsRef.current = jobSet;
      pendingJobIdsRef.current = new Set(jobSet);
      progressTotalFallbackRef.current = Math.max(1, persisted.progress.total);
      automationInProgressRef.current = true;
      sessionFinalizedRef.current = false;
      hilStickyProgressRef.current = {
        segmentIndex: Math.max(0, Math.floor(persisted.progress.current)),
        segmentTotal: Math.max(1, persisted.progress.total),
        lastPercentage:
          persisted.progress.total > 0
            ? Math.round(
                (persisted.progress.current / persisted.progress.total) * 100,
              )
            : 0,
      };
      setAutomationInProgress(true);
      setAutomationProgress(persisted.progress);
      setAutomationLatestStatusText(persisted.latestStatusText || 'Resuming retry…');
      setAutomationLatestStepLabel(persisted.latestStepLabel || 'Resuming');
      setAutomationLastUpdatedAt(persisted.timestamp);
      setProgressPercentageOverride(
        persisted.progress.total > 0
          ? Math.round((persisted.progress.current / persisted.progress.total) * 100)
          : 0,
      );
      setAutomationSocketMessages([]);
      setAutomationCompletionPayload(null);
      queueMicrotask(() => {
        subscribeJobTopicsRef.current([persisted.jobId]);
      });
      onSessionRestoredRef.current?.();
      return;
    }

    if (!enablePersistence) return;

    const persisted = loadNurturingAutomationState(dealerId);
    if (!persisted?.automationInProgress || !persisted.jobId) {
      return;
    }

    const jobSet = new Set([persisted.jobId]);
    activeJobIdsRef.current = jobSet;
    pendingJobIdsRef.current = new Set(jobSet);
    progressTotalFallbackRef.current = Math.max(1, persisted.progress.total);
    automationInProgressRef.current = true;
    sessionFinalizedRef.current = false;
    setAutomationInProgress(true);
    setAutomationProgress(persisted.progress);
    setAutomationSocketMessages(persisted.socketMessages ?? []);
    setAutomationCompletionPayload(null);
  }, [dealerId, canAccess, enablePersistence, persistenceVariant]);

  const aggregateProgressFromJobs = useCallback(() => {
    const entries = Array.from(perJobProgressRef.current.values());
    if (!entries.length) {
      return {
        current: 0,
        total: Math.max(1, progressTotalFallbackRef.current),
      };
    }
    const total = entries.reduce((sum, e) => sum + Math.max(1, e.total), 0);
    const current = entries.reduce((sum, e) => sum + Math.max(0, e.current), 0);
    return {
      current: Math.min(current, total),
      total: Math.max(1, total),
    };
  }, []);

  const finalizeSession = useCallback(
    (completionData: unknown) => {
      if (sessionFinalizedRef.current) return;
      sessionFinalizedRef.current = true;

      const activeDealerId = dealerIdRef.current;
      automationInProgressRef.current = false;
      activeJobIdsRef.current = new Set();
      pendingJobIdsRef.current = new Set();
      perJobProgressRef.current = new Map();
      unsubscribeJobTopicsRef.current();
      setAutomationInProgress(false);
      setAutomationCompletionPayload(completionData);

      if (activeDealerId && enablePersistenceRef.current) {
        clearNurturingAutomationState(activeDealerId);
      }
      if (activeDealerId && persistenceVariantRef.current === 'hil-retry-all') {
        clearHilRetryAllState(activeDealerId);
      }
      setProgressPercentageOverride(null);

      const completionMessageRaw =
        getNurturingAutomationSocketMessage(completionData).message;
      const completionMessage =
        typeof completionMessageRaw === 'string'
          ? completionMessageRaw.trim()
          : '';
      if (completionMessage) {
        dispatch(
          notificationAlertActions.addNotification({
            open: true,
            severity: 'success',
            message: completionMessage,
          }),
        );
      }

      for (const item of invalidatePrefixesOnCompleteRef.current) {
        void apiService.invalidateCacheByPrefix(
          item.prefix,
          item.method ?? 'GET',
        );
      }

      onCompleteRef.current?.();
    },
    [dispatch],
  );

  aggregateProgressFromJobsRef.current = aggregateProgressFromJobs;
  finalizeSessionRef.current = finalizeSession;

  const subscribeJobTopics = useCallback((jobIds: string[]) => {
    const service = socketServiceRef.current;
    if (!service) return;
    for (const rawId of jobIds) {
      const jobId = String(rawId).trim();
      if (!jobId || subscribedJobIdsRef.current.has(jobId)) continue;
      service.subscribeToJob(jobId);
      subscribedJobIdsRef.current.add(jobId);
    }
  }, []);

  const unsubscribeJobTopics = useCallback(() => {
    const service = socketServiceRef.current;
    Array.from(subscribedJobIdsRef.current).forEach(jobId => {
      service?.unsubscribeFromJob(jobId);
    });
    subscribedJobIdsRef.current = new Set();
  }, []);
  unsubscribeJobTopicsRef.current = unsubscribeJobTopics;
  const subscribeJobTopicsRef = useRef(subscribeJobTopics);
  subscribeJobTopicsRef.current = subscribeJobTopics;

  const prepareSession = useCallback(
    (session: PrepareNurturingAutomationSessionOptions) => {
      const total = Math.max(1, session.progressTotal ?? 1);
      progressTotalFallbackRef.current = total;
      activeJobIdsRef.current = new Set();
      pendingJobIdsRef.current = new Set();
      perJobProgressRef.current = new Map();
      setAutomationCompletionPayload(null);
      setAutomationSocketMessages([]);
      setAutomationLatestStatusText('Starting retry…');
      setAutomationLatestStepLabel('Starting');
      setAutomationLastUpdatedAt(Date.now());
      setProgressPercentageOverride(0);
      automationInProgressRef.current = true;
      setAutomationInProgress(true);
      setAutomationProgress({ current: 0, total });
      sessionFinalizedRef.current = false;
      hilStickyProgressRef.current = {
        segmentIndex: 0,
        segmentTotal: total,
        lastPercentage: 0,
      };
    },
    [],
  );

  const startSession = useCallback(
    (session: StartNurturingAutomationSessionOptions) => {
      const jobIds = session.jobIds.map(id => String(id).trim()).filter(Boolean);
      const jobSet = new Set(jobIds);
      const primaryJobId = session.primaryJobId?.trim() || jobIds[0] || '';
      const total = Math.max(1, session.progressTotal ?? 1);

      progressTotalFallbackRef.current = total;
      activeJobIdsRef.current = jobSet;
      pendingJobIdsRef.current = new Set(jobSet);
      perJobProgressRef.current = new Map();
      sessionFinalizedRef.current = false;
      hilStickyProgressRef.current = {
        segmentIndex: 0,
        segmentTotal: total,
        lastPercentage: 0,
      };

      setAutomationCompletionPayload(null);
      setAutomationSocketMessages([]);
      setAutomationLatestStatusText('');
      automationInProgressRef.current = true;
      setAutomationInProgress(true);
      setAutomationProgress({ current: 0, total });

      subscribeJobTopics(jobIds);

      if (
        persistenceVariantRef.current === 'hil-retry-all' &&
        dealerId &&
        primaryJobId
      ) {
        saveHilRetryAllState(dealerId, {
          automationInProgress: true,
          retryAllInProgress: true,
          jobId: primaryJobId,
          progress: { current: 0, total },
          latestStatusText: 'Retry in progress…',
          latestStepLabel: 'In progress',
        });
      }

      if (enablePersistence && dealerId && primaryJobId) {
        saveNurturingAutomationState(dealerId, {
          automationInProgress: true,
          jobId: primaryJobId,
          progress: { current: 0, total },
          socketMessages: [],
        });
      }
    },
    [dealerId, enablePersistence, subscribeJobTopics],
  );

  const resetSession = useCallback(() => {
    automationInProgressRef.current = false;
    activeJobIdsRef.current = new Set();
    pendingJobIdsRef.current = new Set();
    perJobProgressRef.current = new Map();
    sessionFinalizedRef.current = false;
    hilStickyProgressRef.current = {
      segmentIndex: 0,
      segmentTotal: 1,
      lastPercentage: 0,
    };
    unsubscribeJobTopics();
    setAutomationInProgress(false);
    setAutomationProgress({ current: 0, total: 1 });
    setAutomationSocketMessages([]);
    setAutomationLatestStatusText('');
    setAutomationCompletionPayload(null);
    if (dealerId && enablePersistence) {
      clearNurturingAutomationState(dealerId);
    }
  }, [dealerId, enablePersistence, unsubscribeJobTopics]);

  useEffect(() => {
    if (!dealerId || !canAccess) return undefined;

    const socketUrl =
      process.env.REACT_APP_WS_URL ||
      process.env.REACT_APP_API_URL ||
      'http://localhost:3000';
    const socketConfig = {
      url: socketUrl,
      autoConnect: true,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    };

    socketServiceManager.initializeGlobalSocket(socketConfig);
    const service = EmailTemplatesSocketService.getInstance(socketConfig);
    socketServiceRef.current = service;
    socketServiceManager.registerService(service);
    service.connect();

    const callback = (eventName: string, data: unknown) => {
      if (!eventName.startsWith(NURTURING_AUTOMATION_EVENT_PREFIX)) return;

      const isCompletionEvent = eventName === NURTURING_AUTOMATION_EVENT_COMPLETED;
      const sessionHasActiveAutomation =
        automationInProgressRef.current ||
        activeJobIdsRef.current.size > 0 ||
        pendingJobIdsRef.current.size > 0;
      if (!sessionHasActiveAutomation) return;

      if (!isCompletionEvent && !automationInProgressRef.current) return;

      const eventJobId = getNurturingAutomationJobId(data);
      const trackedJobIds = activeJobIdsRef.current;
      const pendingJobIds = pendingJobIdsRef.current;

      if (eventJobId && trackedJobIds.size > 0 && !trackedJobIds.has(eventJobId)) {
        return;
      }

      const activeDealerId = dealerIdRef.current;
      const isHilLive = persistenceVariantRef.current === 'hil-retry-all';

      const applyHilLiveUpdate = (isCompletion: boolean) => {
        const sticky = hilStickyProgressRef.current;
        const live = getAttentionRetryAllLiveProgress(data, eventName, {
          segmentIndex: sticky.segmentIndex,
          segmentTotal: sticky.segmentTotal,
          progressTotalFallback: progressTotalFallbackRef.current,
          lastPercentage: sticky.lastPercentage,
        }, { coarseUi: true });
        const msg = getNurturingAutomationSocketMessage(data);
        if (typeof msg.segment_index === 'number' && msg.segment_index > 0) {
          sticky.segmentIndex = msg.segment_index;
        }
        if (typeof msg.segment_total === 'number' && msg.segment_total > 0) {
          sticky.segmentTotal = msg.segment_total;
        } else if (typeof msg.total_leads === 'number' && msg.total_leads > 0) {
          sticky.segmentTotal = msg.total_leads;
        }
        const percentage = isCompletion ? 100 : live.percentage;
        sticky.lastPercentage = percentage;

        const now = Date.now();
        const displayStatusText =
          typeof msg.status_text === 'string' && msg.status_text.trim()
            ? msg.status_text.trim()
            : live.statusText;
        setAutomationLatestStatusText(displayStatusText);
        setAutomationLatestStepLabel(live.stepLabel);
        setAutomationLastUpdatedAt(now);
        setProgressPercentageOverride(percentage);
        setAutomationProgress({
          current: isCompletion
            ? live.totalSegments
            : Math.min(
                live.totalSegments,
                Math.max(0, Math.round(live.progressUnits * 10) / 10),
              ),
          total: live.totalSegments,
        });
        if (activeDealerId) {
          updateHilRetryAllLiveStatus(activeDealerId, {
            progress: {
              current: isCompletion ? live.totalSegments : live.completedSegments,
              total: live.totalSegments,
            },
            latestStatusText: displayStatusText,
            latestStepLabel: live.stepLabel,
          });
        }
      };

      if (isHilLive) {
        const msg = getNurturingAutomationSocketMessage(data);
        const step = typeof msg.step === 'string' ? msg.step : '';
        const sticky = hilStickyProgressRef.current;

        if (typeof msg.segment_index === 'number' && msg.segment_index > 0) {
          sticky.segmentIndex = msg.segment_index;
        }
        if (typeof msg.segment_total === 'number' && msg.segment_total > 0) {
          sticky.segmentTotal = msg.segment_total;
        } else if (typeof msg.total_leads === 'number' && msg.total_leads > 0) {
          sticky.segmentTotal = msg.total_leads;
        }

        if (
          !isCompletionEvent &&
          !shouldUpdateAttentionRetryAllUi(step, eventName, false)
        ) {
          return;
        }

        applyHilLiveUpdate(isCompletionEvent);
        if (!isCompletionEvent) {
          return;
        }

        if (eventJobId) {
          pendingJobIds.delete(eventJobId);
        } else if (trackedJobIds.size === 1) {
          pendingJobIds.clear();
        }

        const allJobsDone =
          trackedJobIds.size > 0 && pendingJobIds.size === 0;
        if (allJobsDone) {
          finalizeSessionRef.current(data);
        }
        return;
      }

      const socketMessage: NurturingAutomationSocketMessage = {
        eventName,
        data,
        timestamp: Date.now(),
      };
      setAutomationSocketMessages(prev => [...prev, socketMessage].slice(-100));
      const statusLine = getNurturingAutomationStatusText(data);
      if (statusLine) {
        setAutomationLatestStatusText(statusLine);
      }
      if (activeDealerId && enablePersistenceRef.current) {
        appendNurturingAutomationSocketMessage(activeDealerId, socketMessage);
      }

      const { current, total } = getNurturingAutomationLeadCounts(data);
      if (
        eventName === NURTURING_AUTOMATION_EVENT_REQUEST_RECEIVED ||
        eventName === NURTURING_AUTOMATION_EVENT_DEALER_PROGRESS ||
        eventName === NURTURING_AUTOMATION_EVENT_LEAD_PROGRESS
      ) {
        if (eventJobId) {
          const prevJob = perJobProgressRef.current.get(eventJobId) ?? {
            current: 0,
            total: progressTotalFallbackRef.current,
          };
          const totalResolved =
            total != null
              ? Math.max(1, total)
              : prevJob.total > 0
                ? prevJob.total
                : progressTotalFallbackRef.current;
          const currentResolved =
            current != null
              ? Math.min(Math.max(0, current), totalResolved)
              : prevJob.current;
          perJobProgressRef.current.set(eventJobId, {
            current: currentResolved,
            total: totalResolved,
          });
        }

        setAutomationProgress(() => {
          const next = eventJobId
            ? aggregateProgressFromJobsRef.current()
            : {
                current:
                  current != null
                    ? Math.min(
                        Math.max(0, current),
                        Math.max(1, progressTotalFallbackRef.current),
                      )
                    : 0,
                total: Math.max(
                  1,
                  total ?? progressTotalFallbackRef.current,
                ),
              };
          if (activeDealerId && enablePersistenceRef.current) {
            updateNurturingAutomationProgress(activeDealerId, next);
          }
          return next;
        });
      }

      if (eventName === NURTURING_AUTOMATION_EVENT_COMPLETED) {
        if (eventJobId) {
          pendingJobIds.delete(eventJobId);
          const totalResolved =
            total != null
              ? Math.max(1, total)
              : progressTotalFallbackRef.current;
          const currentResolved =
            current != null
              ? Math.min(Math.max(0, current), totalResolved)
              : totalResolved;
          perJobProgressRef.current.set(eventJobId, {
            current: currentResolved,
            total: totalResolved,
          });
        } else if (trackedJobIds.size === 1) {
          pendingJobIds.clear();
        }

        setAutomationProgress(prev => {
          const aggregated = eventJobId
            ? aggregateProgressFromJobsRef.current()
            : {
                current:
                  current != null
                    ? Math.min(
                        Math.max(0, current),
                        Math.max(1, prev.total),
                      )
                    : prev.total,
                total: Math.max(1, total ?? prev.total),
              };
          return aggregated;
        });

        const allJobsDone =
          trackedJobIds.size > 0 && pendingJobIds.size === 0;
        if (allJobsDone) {
          finalizeSessionRef.current(data);
        }
      }
    };

    service.subscribeToDealer(dealerId, callback);
    if (activeJobIdsRef.current.size > 0) {
      subscribeJobTopics(Array.from(activeJobIdsRef.current));
    }

    return () => {
      service.unsubscribeFromDealer(dealerId, callback);
    };
  }, [canAccess, dealerId]);

  const automationProgressPercentage = useMemo(() => {
    if (progressPercentageOverride != null) {
      return Math.min(100, Math.max(0, progressPercentageOverride));
    }
    const total = Math.max(automationProgress.total, 1);
    return Math.min(
      100,
      Math.max(0, Math.round((automationProgress.current / total) * 100)),
    );
  }, [
    automationProgress.current,
    automationProgress.total,
    progressPercentageOverride,
  ]);

  const automationStreamingDataForCard = useMemo(() => {
    const totalSections = Math.max(automationProgress.total, 1);
    return {
      message: {
        sections: Array.from({ length: totalSections }, (_, i) => ({
          index: i + 1,
          main: `Lead ${i + 1}`,
          sub_text: '',
        })),
      },
    };
  }, [automationProgress.total]);

  const automationCurrentSectionForCard = useMemo(
    () => ({
      index: Math.min(
        Math.max(1, automationProgress.current || 1),
        Math.max(automationProgress.total, 1),
      ),
      type: 'step' as const,
      main: automationLatestStatusText || progressTitle,
      sub_text:
        progressSubtitle ??
        (automationLatestStatusText
          ? `${automationProgress.current}/${Math.max(
              automationProgress.total,
              1,
            )} ${progressCountLabel} processed`
          : `${automationProgress.current}/${Math.max(
              automationProgress.total,
              1,
            )} ${progressCountLabel} processed`),
    }),
    [
      automationLatestStatusText,
      automationProgress.current,
      automationProgress.total,
      progressCountLabel,
      progressSubtitle,
      progressTitle,
    ],
  );

  return {
    automationInProgress,
    automationProgressPercentage,
    automationProgressCurrent: automationProgress.current,
    automationProgressTotal: automationProgress.total,
    automationStreamingDataForCard,
    automationCurrentSectionForCard,
    automationSocketMessages,
    automationLatestStatusText,
    automationLatestStepLabel,
    automationLastUpdatedAt,
    automationCompletionPayload,
    startSession,
    prepareSession,
    resetSession,
  };
}
